import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  FormLabel,
  TextField,
  Button as MuiButton,
  Chip,
  Stack,
  CircularProgress,
  IconButton,
  Grid,
  Typography,
  LinearProgress,
  TablePagination,
} from "@mui/material";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import EditIcon from "@mui/icons-material/Edit";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import type { AxiosInstance } from "axios";
import { CAutocompleteMultiFreeSoloTextField } from "@/components/TextField/CAutocompleteMultiFreeSoloTextField";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { AdditionalFieldsModal } from "./AdditionalFieldsModal";
import type { AdditionalFieldConfig } from "@/features/accountSettings/types/additionalFields";
import type { AdditionalFieldsFormData } from "../types";
import { DeletePlotDetailFileConfirmModal } from "@/features/mapPlotRequest/components/DeletePlotDetailFileConfirmModal";
import { useMapPlotRequestDetailFilesWithApiClient } from "@/features/mapPlotRequest/hooks/useMapPlotRequestDetailFiles";
import {
  extractMapPlotRequestDetailFileErrorMessage,
  isPreviewableMapPlotRequestDetailFileContentType,
  isSupportedMapPlotRequestDetailFile,
} from "@/features/mapPlotRequest/utils/mapPlotRequestDetailFiles";
import type { MapPlotRequestDetailFile } from "@/features/map/types";
import { formatNumberWithComma } from "@/utils/formatNumber";

interface TagFormData {
  tags: string[];
}

interface TagUIState {
  isEditing: boolean;
  displayTags: string[];
}

interface RemarksUIState {
  isEditing: boolean;
  value: string;
}

interface SavedPlotMemoModalProps {
  apiClient: AxiosInstance;
  open: boolean;
  onClose: () => void;
  initialTags: string[];
  initialRemarks: string;
  initialAdditionalFields: AdditionalFieldsFormData;
  onTagsSave: (tags: string[]) => Promise<void>;
  onRemarksSave: (remarks: string) => Promise<void>;
  onAdditionalFieldsSave: (data: AdditionalFieldsFormData) => Promise<void>;
  detailId?: number;
  onOpenFilesPage?: () => void;
  availableFields?: AdditionalFieldConfig;
  title?: string;
}

const isPreviewableFile = (file: MapPlotRequestDetailFile): boolean => {
  return isPreviewableMapPlotRequestDetailFileContentType(file.contentType);
};

/**
 * 保存済みプロットのメモ（タグ・備考・カスタム項目）を編集するモーダル
 * API保存機能付き
 */
export const SavedPlotMemoModal: React.FC<SavedPlotMemoModalProps> = ({
  apiClient,
  open,
  onClose,
  initialTags,
  initialRemarks,
  initialAdditionalFields,
  onTagsSave,
  onRemarksSave,
  onAdditionalFieldsSave,
  detailId,
  onOpenFilesPage,
  availableFields,
  title = "メモを編集",
}) => {
  const hasAvailableFields =
    availableFields?.fields && availableFields.fields.length > 0;

  // タグ管理用のform
  const { control, getValues, reset } = useForm<TagFormData>({
    defaultValues: {
      tags: initialTags,
    },
  });

  // タグUI状態管理
  const [tagUIState, setTagUIState] = useState<TagUIState>({
    isEditing: false,
    displayTags: initialTags,
  });

  // 備考UI状態管理
  const [remarksUIState, setRemarksUIState] = useState<RemarksUIState>({
    isEditing: false,
    value: initialRemarks,
  });
  const [savedRemarks, setSavedRemarks] = useState(initialRemarks);

  // 追加項目モーダル状態管理
  const [additionalFieldsModalOpen, setAdditionalFieldsModalOpen] =
    useState(false);

  // 追加項目の現在値を管理（同期用）
  const [currentAdditionalFields, setCurrentAdditionalFields] = useState(
    initialAdditionalFields
  );

  // 保存中状態
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [isSavingRemarks, setIsSavingRemarks] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedFilesMessage, setSelectedFilesMessage] = useState<
    string | null
  >(null);
  const [previewingFileId, setPreviewingFileId] = useState<number | null>(null);
  const [downloadingFileId, setDownloadingFileId] = useState<number | null>(
    null
  );
  const [deleteTargetFile, setDeleteTargetFile] =
    useState<MapPlotRequestDetailFile | null>(null);
  const [isDeletingFile, setIsDeletingFile] = useState(false);
  const shouldShowFilesSection =
    detailId !== undefined || onOpenFilesPage !== undefined;
  const canManageFiles = detailId !== undefined;
  const {
    files,
    totalCount,
    page,
    pageSize,
    changePage,
    changePageSize,
    isLoading: isLoadingFiles,
    isError: isFilesError,
    refetch: refetchFiles,
    uploadFiles,
    deleteFile,
    getDownloadInfo,
    isUploading,
    uploadProgress,
    failedUploads,
    lastUploadSummary,
    resetUploadState,
  } = useMapPlotRequestDetailFilesWithApiClient(
    apiClient,
    detailId ?? null,
    open && canManageFiles
  );

  // モーダルが開かれたときに初期値をリセット
  useEffect(() => {
    if (open) {
      reset({ tags: initialTags });
      setTagUIState({
        isEditing: false,
        displayTags: initialTags,
      });
      setRemarksUIState({
        isEditing: false,
        value: initialRemarks,
      });
      setSavedRemarks(initialRemarks);
      setCurrentAdditionalFields(initialAdditionalFields);
      setSelectedFiles([]);
      setSelectedFilesMessage(null);
      setPreviewingFileId(null);
      setDownloadingFileId(null);
      setDeleteTargetFile(null);
      resetUploadState();
    }
  }, [
    open,
    initialTags,
    initialRemarks,
    initialAdditionalFields,
    reset,
    resetUploadState,
  ]);

  const handleTagsComplete = async (): Promise<void> => {
    const currentTags = getValues("tags");
    try {
      setIsSavingTags(true);
      await onTagsSave(currentTags);
      setTagUIState({
        isEditing: false,
        displayTags: currentTags,
      });
    } catch (error) {
      // エラーはonTagsSave側でトースト表示されるため、ここでは何もしない
    } finally {
      setIsSavingTags(false);
    }
  };

  const handleRemarksComplete = async (): Promise<void> => {
    try {
      setIsSavingRemarks(true);
      await onRemarksSave(remarksUIState.value);
      setSavedRemarks(remarksUIState.value);
      setRemarksUIState((prev) => ({
        ...prev,
        isEditing: false,
      }));
    } catch (error) {
      // エラーはonRemarksSave側でトースト表示されるため、ここでは何もしない
    } finally {
      setIsSavingRemarks(false);
    }
  };

  const handleRemarksCancel = (): void => {
    setRemarksUIState({
      isEditing: false,
      value: savedRemarks,
    });
  };

  const handleAdditionalFieldsSave = async (
    formData: Record<string, string>
  ): Promise<void> => {
    await onAdditionalFieldsSave(formData);
    setCurrentAdditionalFields(formData);
    setAdditionalFieldsModalOpen(false);
  };

  const handleSelectFiles = (): void => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const pickedFiles = Array.from(event.target.files ?? []);

    if (pickedFiles.length === 0) {
      return;
    }

    const acceptedFiles = pickedFiles.filter((file) =>
      isSupportedMapPlotRequestDetailFile(file.name)
    );
    const rejectedFiles = pickedFiles.filter(
      (file) => !isSupportedMapPlotRequestDetailFile(file.name)
    );

    if (acceptedFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
    }

    if (rejectedFiles.length > 0) {
      setSelectedFilesMessage(
        `${formatNumberWithComma(
          rejectedFiles.length
        )}件は対応していない拡張子のため除外しました。`
      );
    } else {
      setSelectedFilesMessage(null);
    }

    event.target.value = "";
  };

  const handleUploadFiles = async (): Promise<void> => {
    if (!canManageFiles) {
      return;
    }

    if (selectedFiles.length === 0) {
      setSelectedFilesMessage("アップロードするファイルを選択してください。");
      return;
    }

    try {
      const summary = await uploadFiles(selectedFiles);

      if (summary.successCount > 0) {
        toast.success(
          `${formatNumberWithComma(
            summary.successCount
          )}件の資料をアップロードしました`
        );
      }

      if (summary.failedCount > 0) {
        toast.error(
          `${formatNumberWithComma(
            summary.failedCount
          )}件の資料のアップロードに失敗しました`
        );
      }

      setSelectedFiles([]);
    } catch (error) {
      toast.error(
        extractMapPlotRequestDetailFileErrorMessage(
          error,
          "資料のアップロードに失敗しました"
        )
      );
    }
  };

  const openDownloadUrl = (url: string): void => {
    const openedWindow = window.open(url, "_blank", "noopener,noreferrer");

    if (openedWindow === null) {
      window.location.assign(url);
    }
  };

  const handleDownloadFile = async (
    file: MapPlotRequestDetailFile
  ): Promise<void> => {
    if (!canManageFiles) {
      return;
    }

    setDownloadingFileId(file.id);
    try {
      const downloadInfo = await getDownloadInfo(file.id, false);

      openDownloadUrl(downloadInfo.downloadUrl);
    } catch (error) {
      toast.error(
        extractMapPlotRequestDetailFileErrorMessage(
          error,
          "資料のダウンロードに失敗しました"
        )
      );
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handlePreviewFile = async (
    file: MapPlotRequestDetailFile
  ): Promise<void> => {
    if (!canManageFiles || !isPreviewableFile(file)) {
      return;
    }

    const previewWindow: Window | null = window.open("", "_blank");

    if (previewWindow === null) {
      toast.error(
        "ポップアップがブロックされました。ブラウザの設定を確認してください"
      );
      return;
    }

    previewWindow.opener = null;

    setPreviewingFileId(file.id);
    try {
      const downloadInfo = await getDownloadInfo(file.id, true);

      previewWindow.location.assign(downloadInfo.downloadUrl);
    } catch (error) {
      if (previewWindow !== null && !previewWindow.closed) {
        previewWindow.close();
      }

      toast.error(
        extractMapPlotRequestDetailFileErrorMessage(
          error,
          "資料のプレビューに失敗しました"
        )
      );
    } finally {
      setPreviewingFileId(null);
    }
  };

  const handleDeleteFile = async (): Promise<void> => {
    if (!deleteTargetFile) {
      return;
    }

    setIsDeletingFile(true);
    try {
      await deleteFile(deleteTargetFile.id);
      toast.success("資料を削除しました");
      setDeleteTargetFile(null);
    } catch (error) {
      toast.error(
        extractMapPlotRequestDetailFileErrorMessage(
          error,
          "資料の削除に失敗しました"
        )
      );
    } finally {
      setIsDeletingFile(false);
    }
  };

  const isFileActionDisabled =
    isUploading ||
    isDeletingFile ||
    previewingFileId !== null ||
    downloadingFileId !== null;

  return (
    <>
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          // 枠外クリックやESCキーでの閉じるを無効化
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          onClose();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: "400px" },
        }}
      >
        <DialogTitle>
          {title}
          <MuiButton
            variant="outlined"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              minWidth: "auto",
              padding: "4px 12px",
              fontSize: "0.875rem",
            }}
          >
            閉じる
          </MuiButton>
        </DialogTitle>
        <DialogContent dividers>
          {/* タグセクション */}
          <Box mb={3}>
            <FormLabel
              sx={{
                fontSize: "0.875rem",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 1,
              }}
            >
              <LocalOfferIcon fontSize="small" />
              タグ
            </FormLabel>
            <Box ml={0.5}>
              {!tagUIState.isEditing ? (
                <Box>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}
                  >
                    {tagUIState.displayTags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{
                          fontSize: "12px",
                          height: "24px",
                        }}
                      />
                    ))}
                  </Stack>
                  <MuiButton
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setTagUIState((prev) => ({ ...prev, isEditing: true }));
                    }}
                    sx={{
                      padding: "4px 16px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      textTransform: "none",
                    }}
                  >
                    +タグを追加
                  </MuiButton>
                </Box>
              ) : (
                <Box>
                  <CAutocompleteMultiFreeSoloTextField
                    control={control}
                    name="tags"
                    label=""
                    size={SizingWrapperStyle.INHERIT}
                    maxLength={20}
                  />
                  <MuiButton
                    variant="contained"
                    size="small"
                    onClick={() => {
                      void handleTagsComplete();
                    }}
                    sx={{
                      fontSize: "12px",
                      mt: 1,
                      textTransform: "none",
                    }}
                    disabled={isSavingTags}
                  >
                    {isSavingTags ? (
                      <>
                        <CircularProgress
                          size={16}
                          color="inherit"
                          sx={{ mr: 1 }}
                        />
                        保存中...
                      </>
                    ) : (
                      "入力完了"
                    )}
                  </MuiButton>
                </Box>
              )}
            </Box>
          </Box>

          {/* 備考セクション */}
          <Box mb={3}>
            <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
              <FormLabel>
                <NoteAltIcon fontSize={"small"} />
                備考
              </FormLabel>
              <IconButton
                size="small"
                onClick={() => {
                  setRemarksUIState((prev) => ({
                    ...prev,
                    isEditing: true,
                  }));
                }}
                sx={{ ml: 1 }}
                aria-label="編集"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>

            {remarksUIState.isEditing ? (
              <>
                <TextField
                  value={remarksUIState.value}
                  onChange={(e) => {
                    setRemarksUIState((prev) => ({
                      ...prev,
                      value: e.target.value,
                    }));
                  }}
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={4}
                  variant="outlined"
                  sx={{ mt: 1, mb: 1 }}
                  inputProps={{ maxLength: 200 }}
                  size="small"
                />
                <Grid
                  container
                  justifyContent="flex-end"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <Grid item>
                    <MuiButton
                      variant="outlined"
                      size="small"
                      onClick={handleRemarksCancel}
                      disabled={isSavingRemarks}
                    >
                      キャンセル
                    </MuiButton>
                  </Grid>
                  <Grid item>
                    <MuiButton
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => {
                        void handleRemarksComplete();
                      }}
                      disabled={isSavingRemarks}
                    >
                      {isSavingRemarks ? (
                        <>
                          <CircularProgress
                            size={16}
                            color="inherit"
                            sx={{ mr: 1 }}
                          />
                          保存中...
                        </>
                      ) : (
                        "入力完了"
                      )}
                    </MuiButton>
                  </Grid>
                </Grid>
              </>
            ) : (
              <Typography
                ml={2}
                my={1}
                fontSize={"0.8rem"}
                variant={"body1"}
                component={"div"}
                sx={{
                  whiteSpace: "pre-wrap",
                  minHeight: "1.5rem",
                  wordBreak: "break-word",
                }}
              >
                {remarksUIState.value || ""}
              </Typography>
            )}
          </Box>

          {shouldShowFilesSection && (
            <Box mb={3}>
              <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                <FormLabel
                  sx={{
                    fontSize: "0.875rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <AttachFileIcon fontSize="small" />
                  資料
                </FormLabel>
              </Box>
              <Box ml={0.5}>
                {canManageFiles && (
                  <Stack spacing={1.5}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "stretch", sm: "center" }}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        hidden
                        onChange={handleFileChange}
                      />
                      <MuiButton
                        variant="outlined"
                        size="small"
                        onClick={handleSelectFiles}
                        startIcon={<UploadFileIcon fontSize="small" />}
                        disabled={isUploading || isDeletingFile}
                        sx={{ textTransform: "none" }}
                      >
                        ファイル選択
                      </MuiButton>
                      <MuiButton
                        variant="contained"
                        size="small"
                        onClick={() => {
                          void handleUploadFiles();
                        }}
                        disabled={
                          isUploading ||
                          isDeletingFile ||
                          selectedFiles.length === 0
                        }
                        sx={{ textTransform: "none" }}
                      >
                        アップロード
                      </MuiButton>
                      {onOpenFilesPage && (
                        <MuiButton
                          variant="outlined"
                          size="small"
                          onClick={onOpenFilesPage}
                          startIcon={<OpenInNewIcon fontSize="small" />}
                          sx={{ textTransform: "none" }}
                        >
                          資料管理
                        </MuiButton>
                      )}
                    </Stack>

                    {selectedFiles.length > 0 && (
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          maxHeight: 120,
                          overflowY: "auto",
                        }}
                      >
                        <Stack spacing={0.5}>
                          {selectedFiles.map((file, index) => (
                            <Typography
                              key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                              variant="body2"
                              sx={{ overflowWrap: "anywhere" }}
                            >
                              {file.name}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {selectedFilesMessage && (
                      <Typography variant="body2" color="error">
                        {selectedFilesMessage}
                      </Typography>
                    )}

                    {isUploading && (
                      <Stack spacing={0.8}>
                        <LinearProgress
                          variant="determinate"
                          value={
                            uploadProgress.total === 0
                              ? 0
                              : (uploadProgress.processed /
                                  uploadProgress.total) *
                                100
                          }
                        />
                        <Typography variant="body2" color="text.secondary">
                          {`${formatNumberWithComma(
                            uploadProgress.processed
                          )}/${formatNumberWithComma(
                            uploadProgress.total
                          )} 件処理中（成功 ${formatNumberWithComma(
                            uploadProgress.successCount
                          )} / 失敗 ${formatNumberWithComma(
                            uploadProgress.failedCount
                          )}）`}
                        </Typography>
                      </Stack>
                    )}

                    {lastUploadSummary && !isUploading && (
                      <Typography variant="body2" color="text.secondary">
                        {`直近結果: 成功 ${formatNumberWithComma(
                          lastUploadSummary.successCount
                        )}件 / 失敗 ${formatNumberWithComma(
                          lastUploadSummary.failedCount
                        )}件`}
                      </Typography>
                    )}

                    {failedUploads.length > 0 && (
                      <Box
                        sx={{
                          p: 1.5,
                          border: "1px solid",
                          borderColor: "error.main",
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="error"
                          sx={{ mb: 1 }}
                        >
                          アップロードに失敗したファイルがあります。
                        </Typography>
                        <Stack spacing={0.5}>
                          {failedUploads.map((failedItem, index) => (
                            <Typography
                              key={`${failedItem.file.name}-${failedItem.file.size}-${failedItem.file.lastModified}-${index}`}
                              variant="caption"
                              color="text.secondary"
                              sx={{ overflowWrap: "anywhere" }}
                            >
                              {`${failedItem.file.name}: ${failedItem.message}`}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    <Box
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      {isLoadingFiles ? (
                        <Box
                          sx={{
                            py: 3,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <CircularProgress size={24} />
                        </Box>
                      ) : isFilesError ? (
                        <Stack spacing={1} alignItems="center" sx={{ py: 2 }}>
                          <Typography variant="body2" color="error">
                            資料一覧の取得に失敗しました。
                          </Typography>
                          <MuiButton
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              void refetchFiles();
                            }}
                          >
                            再読み込み
                          </MuiButton>
                        </Stack>
                      ) : files.length === 0 ? (
                        <Box sx={{ px: 1.5, py: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            資料はありません。
                          </Typography>
                        </Box>
                      ) : (
                        <Stack
                          divider={
                            <Box
                              sx={{
                                borderTop: "1px solid",
                                borderColor: "divider",
                              }}
                            />
                          }
                        >
                          {files.map((file) => {
                            const isPreviewable = isPreviewableFile(file);
                            const isPreviewing = previewingFileId === file.id;
                            const isDownloading = downloadingFileId === file.id;

                            return (
                              <Box
                                key={file.id}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 1,
                                  px: 1.5,
                                  py: 1,
                                }}
                              >
                                {isPreviewable ? (
                                  <MuiButton
                                    variant="text"
                                    size="small"
                                    onClick={() => {
                                      void handlePreviewFile(file);
                                    }}
                                    disabled={isFileActionDisabled}
                                    sx={{
                                      minWidth: 0,
                                      p: 0,
                                      justifyContent: "flex-start",
                                      textAlign: "left",
                                      textTransform: "none",
                                      overflowWrap: "anywhere",
                                    }}
                                  >
                                    {file.displayName}
                                  </MuiButton>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    sx={{ overflowWrap: "anywhere" }}
                                  >
                                    {file.displayName}
                                  </Typography>
                                )}
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  sx={{ flexShrink: 0 }}
                                >
                                  <IconButton
                                    size="small"
                                    aria-label={`${file.displayName}をダウンロード`}
                                    onClick={() => {
                                      void handleDownloadFile(file);
                                    }}
                                    disabled={isFileActionDisabled}
                                  >
                                    {isDownloading ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      <DownloadIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                  {isPreviewable && (
                                    <IconButton
                                      size="small"
                                      aria-label={`${file.displayName}をプレビュー`}
                                      onClick={() => {
                                        void handlePreviewFile(file);
                                      }}
                                      disabled={isFileActionDisabled}
                                    >
                                      {isPreviewing ? (
                                        <CircularProgress size={18} />
                                      ) : (
                                        <VisibilityOutlinedIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  )}
                                  <IconButton
                                    size="small"
                                    color="error"
                                    aria-label={`${file.displayName}を削除`}
                                    onClick={() => {
                                      setDeleteTargetFile(file);
                                    }}
                                    disabled={isFileActionDisabled}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Box>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>

                    {!isLoadingFiles && !isFilesError && totalCount > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <TablePagination
                          component="div"
                          count={totalCount}
                          page={page}
                          rowsPerPage={pageSize}
                          rowsPerPageOptions={[20, 50, 100]}
                          onPageChange={(_, newPage) => {
                            changePage(newPage);
                          }}
                          onRowsPerPageChange={(event) => {
                            changePageSize(Number(event.target.value));
                          }}
                          labelRowsPerPage="ページあたりの行数:"
                          labelDisplayedRows={({ from, to, count }) =>
                            `${from.toLocaleString()}〜${to.toLocaleString()} / ${count.toLocaleString()}`
                          }
                        />
                      </Box>
                    )}
                  </Stack>
                )}

                {!canManageFiles && onOpenFilesPage && (
                  <MuiButton
                    variant="outlined"
                    size="small"
                    onClick={onOpenFilesPage}
                    startIcon={<OpenInNewIcon fontSize="small" />}
                    sx={{
                      padding: "4px 16px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      textTransform: "none",
                    }}
                  >
                    資料管理
                  </MuiButton>
                )}
              </Box>
            </Box>
          )}

          {/* カスタム項目セクション */}
          {hasAvailableFields && (
            <Box>
              <Box display="flex" alignItems="center">
                <FormLabel
                  sx={{
                    fontSize: "0.875rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <LibraryAddIcon fontSize="small" />
                  カスタム項目
                </FormLabel>
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    color: "primary.main",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: "0.875rem",
                    "&:hover": {
                      color: "primary.dark",
                    },
                  }}
                  onClick={() => {
                    setAdditionalFieldsModalOpen(true);
                  }}
                >
                  編集
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <DeletePlotDetailFileConfirmModal
        open={deleteTargetFile !== null}
        fileName={deleteTargetFile?.displayName ?? ""}
        isDeleting={isDeletingFile}
        onClose={() => {
          if (!isDeletingFile) {
            setDeleteTargetFile(null);
          }
        }}
        onDelete={() => {
          void handleDeleteFile();
        }}
      />

      {/* カスタム項目編集モーダル */}
      <AdditionalFieldsModal
        open={additionalFieldsModalOpen}
        onClose={() => {
          setAdditionalFieldsModalOpen(false);
        }}
        availableFields={availableFields}
        initialValues={currentAdditionalFields}
        onSave={handleAdditionalFieldsSave}
      />
    </>
  );
};
